import { useEffect, useState } from 'react'
import Icon from '../components/Icon'
import { CELEBRATE, type Moment } from './feel'

/*
 * The brand's four colours, so a burst looks like this app and not like a
 * party-supplies catalogue.
 */
const COLOURS = ['#0E6B72', '#F59E0B', '#FF5A5F', '#2F9AA2']
const SPARKS = 28

interface Burst extends Moment {
  id: number
  sparks: { dx: number; dy: number; rot: number; colour: string; round: boolean; delay: number }[]
}

/**
 * The congratulations card and the sparks behind it.
 *
 * Mounted once at the root and woken by `celebrate()` in feel.ts, so a handler
 * deep in a form can say "that mattered" without owning any of the drawing.
 *
 * The card is a live region, so a screen reader hears the words. The sparks
 * are decoration and hidden from it, and with reduced motion they do not
 * render at all; the card still appears, without the bounce.
 */
export default function Celebration() {
  const [burst, setBurst] = useState<Burst | null>(null)

  useEffect(() => {
    const on = (e: Event) => {
      const { title, line } = (e as CustomEvent<Moment>).detail
      setBurst({
        id: Date.now(),
        title,
        line,
        sparks: Array.from({ length: SPARKS }, (_, i) => {
          // Evenly round the circle with a little jitter, so the burst is full
          // without looking like a clock face.
          const angle = (i / SPARKS) * Math.PI * 2 + (Math.random() - 0.5) * 0.4
          const reach = 110 + Math.random() * 120
          return {
            dx: Math.cos(angle) * reach,
            dy: Math.sin(angle) * reach,
            rot: (Math.random() - 0.5) * 540,
            colour: COLOURS[i % COLOURS.length] as string,
            round: i % 3 === 0,
            delay: Math.random() * 0.08,
          }
        }),
      })
    }
    window.addEventListener(CELEBRATE, on)
    return () => window.removeEventListener(CELEBRATE, on)
  }, [])

  useEffect(() => {
    if (!burst) return
    const timer = setTimeout(() => setBurst(null), 3200)
    return () => clearTimeout(timer)
  }, [burst])

  if (!burst) return <div className="w-sr" role="status" aria-live="polite" />

  return (
    <div className="w-celebrate" key={burst.id} onClick={() => setBurst(null)}>
      <div className="w-sparks" aria-hidden="true">
        {burst.sparks.map((s, i) => (
          <span
            key={i}
            className={s.round ? 'w-spark round' : 'w-spark'}
            style={{
              '--dx': `${s.dx}px`,
              '--dy': `${s.dy}px`,
              '--rot': `${s.rot}deg`,
              '--delay': `${s.delay}s`,
              background: s.colour,
            } as React.CSSProperties}
          />
        ))}
      </div>
      <div className="w-cheer" role="status" aria-live="polite">
        <span className="w-cheer-icon"><Icon name="sparkle" size={28} /></span>
        <strong>{burst.title}</strong>
        <span>{burst.line}</span>
      </div>
    </div>
  )
}
